import React, { useState, useImperativeHandle, forwardRef } from 'react';
import {
  Box,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert
} from '@mui/material';
import {
  Share as ShareIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  AdminPanelSettings as AdminIcon,
  EditOutlined as EditAccessIcon,
  DeleteOutline as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { isUserSuperAdminSelector } from 'app/selectors/userSelectors';
import { companiesSelector } from 'app/selectors/companiesSelector';
import { addInfo, addError } from 'app/actions/snackbar/snackbar';
import {
  usePersonaGetPersonaCompanies,
  usePersonaUpdatePersonaCompanyAccess,
  usePersonaUnsharePersonaFromCompanies
} from '@/api-gen/api';
import type { PersonaCompanyDto } from '@/api-gen/api.schemas';
import { AccessLevel } from '@/api-gen/api.schemas';

import type { I18nPersonasNs, I18nCommonNs } from '@/i18n/dictionaries/interfaces';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import { useTranslation } from 'react-i18next';

interface SharedCompaniesDisplayProps {
  personaId?: number;
  onRefresh?: () => void;
  otherOrganizationsCount?: number;
}

export interface SharedCompaniesDisplayRef {
  refetch: () => void;
}

const SharedCompaniesDisplay = forwardRef<SharedCompaniesDisplayRef, SharedCompaniesDisplayProps>(({ 
  personaId,
  onRefresh,
  otherOrganizationsCount
}, ref) => {
  const [selectedCompany, setSelectedCompany] = useState<PersonaCompanyDto | null>(null);
  const [selectedAccess, setSelectedAccess] = useState<AccessLevel>(AccessLevel.CanRead);
  const [isAccessDialogOpen, setIsAccessDialogOpen] = useState(false);
  const [isUnshareDialogOpen, setIsUnshareDialogOpen] = useState(false);
  const [companyToUnshare, setCompanyToUnshare] = useState<PersonaCompanyDto | null>(null);

  const isSuperAdmin = useSelector(isUserSuperAdminSelector);
  const dispatch = useDispatch();
  const allCompanies = useSelector(companiesSelector);

  const { t: translate } = useTranslation(I18nNamespace.Personas);
  const { t: translateCommon } = useTranslation(I18nNamespace.Common);

  // Function to get company name by ID
  const getCompanyName = (companyId: number) => {
    const company = allCompanies.find(c => c.id === companyId);
    return company?.name || `Company ${companyId}`;
  };

  // Fetch shared companies (superadmin only — non-superadmins get 403)
  const { data: sharedCompaniesResponse, isLoading, refetch } = usePersonaGetPersonaCompanies(personaId!, {
    query: {
      enabled: !!personaId && isSuperAdmin
    }
  });

  const sharedCompanies = Array.isArray(sharedCompaniesResponse?.data)
    ? sharedCompaniesResponse.data
    : Array.isArray(sharedCompaniesResponse)
    ? sharedCompaniesResponse
    : [];

  const displayedOtherOrganizationsCount = Math.max(0, otherOrganizationsCount ?? 0);
  const shouldShowSharingSummary = displayedOtherOrganizationsCount > 0;
  const sharingSummaryText = shouldShowSharingSummary
    ? displayedOtherOrganizationsCount === 1
      ? translate(nameof.full<I18nPersonasNs>((n) => n.sharing.sharedWithOrganizationCountSingular), {
        count: displayedOtherOrganizationsCount
      })
      : translate(nameof.full<I18nPersonasNs>((n) => n.sharing.sharedWithOrganizationCountPlural), {
        count: displayedOtherOrganizationsCount
      })
    : '';

  // Expose refetch function via ref
  useImperativeHandle(ref, () => ({
    refetch
  }), [refetch]);

  // Update access mutation
  const updateAccessMutation = usePersonaUpdatePersonaCompanyAccess({
    mutation: {
      onSuccess: () => {
        dispatch(addInfo({ infoText: translate(nameof.full<I18nPersonasNs>((n) => n.messages.accessUpdatedSuccess)) }));
        refetch();
        if (onRefresh) onRefresh();
        handleCloseAccessDialog();
      },
      onError: (error: any) => {
        dispatch(addError({ errorText: error?.message || translate(nameof.full<I18nPersonasNs>((n) => n.messages.updateAccessError)) }));
      }
    }
  });

  // Unshare mutation
  const unshareMutation = usePersonaUnsharePersonaFromCompanies({
    mutation: {
      onSuccess: () => {
        dispatch(addInfo({ infoText: translate(nameof.full<I18nPersonasNs>((n) => n.messages.personaUnsharedSuccess)) }));
        refetch();
        if (onRefresh) onRefresh();
        handleCloseUnshareDialog();
      },
      onError: (error: any) => {
        dispatch(addError({ errorText: error?.message || translate(nameof.full<I18nPersonasNs>((n) => n.messages.unsharePersonaError)) }));
      }
    }
  });

  const getAccessLevelDisplay = (accessLevel?: AccessLevel) => {
    switch (accessLevel) {
      case AccessLevel.CanEdit:
        return {
          icon: <EditIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />,
          text: translate(nameof.full<I18nPersonasNs>((n) => n.accessLevels.canEdit)),
          color: 'primary.main'
        };
      case AccessLevel.CanAdministrate:
        return {
          icon: <AdminIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />,
          text: translate(nameof.full<I18nPersonasNs>((n) => n.accessLevels.canAdministrate)),
          color: 'secondary.main'
        };
      default:
        return {
          icon: <ViewIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />,
          text: translate(nameof.full<I18nPersonasNs>((n) => n.accessLevels.canRead)),
          color: 'text.secondary'
        };
    }
  };

  const handleOpenAccessDialog = (company: PersonaCompanyDto) => {
    setSelectedCompany(company);
    setSelectedAccess(company.access || AccessLevel.CanRead);
    setIsAccessDialogOpen(true);
  };

  const handleCloseAccessDialog = () => {
    setIsAccessDialogOpen(false);
    setSelectedCompany(null);
  };

  const handleOpenUnshareDialog = (company: PersonaCompanyDto) => {
    setCompanyToUnshare(company);
    setIsUnshareDialogOpen(true);
  };

  const handleCloseUnshareDialog = () => {
    setCompanyToUnshare(null);
    setIsUnshareDialogOpen(false);
  };

  const handleUpdateAccess = () => {
    if (!selectedCompany || !personaId) return;

    updateAccessMutation.mutate({
      personaId: personaId,
      companyId: selectedCompany.companyId!,
      data: selectedAccess
    });
  };

  const handleConfirmUnshare = () => {
    if (!companyToUnshare || !personaId) return;

    unshareMutation.mutate({
      id: personaId,
      data: [companyToUnshare.companyId!]
    });
  };

  if (isSuperAdmin && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (sharedCompanies.length === 0) {
    return (
      <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        {shouldShowSharingSummary && (
          <Typography variant="subtitle2" color="text.secondary">
            <ShareIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
            {sharingSummaryText}
          </Typography>
        )}
        {!shouldShowSharingSummary && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {translate(nameof.full<I18nPersonasNs>((n) => n.sharing.notSharedWithAnyCompanies))}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
          {shouldShowSharingSummary && (
            <Typography variant="subtitle2" gutterBottom>
              <ShareIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
              {sharingSummaryText}
            </Typography>
          )}
        </Box>

        <TableContainer component={Paper} elevation={0} variant="outlined">
          <Table size="small" aria-label="Shared companies table">
            <TableHead>
              <TableRow>
                <TableCell>{translate(nameof.full<I18nPersonasNs>((n) => n.sharing.company))}</TableCell>
                <TableCell>{translate(nameof.full<I18nPersonasNs>((n) => n.sharing.access))}</TableCell>
                {isSuperAdmin && <TableCell align="right">{translate(nameof.full<I18nPersonasNs>((n) => n.sharing.actions))}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {sharedCompanies.map((company) => {
                const { icon, text: accessText, color: accessColor } = getAccessLevelDisplay(company.access);
                return (
                  <TableRow hover key={company.companyId}>
                    <TableCell component="th" scope="row">
                      {getCompanyName(company.companyId!)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', color: accessColor }}>
                        {icon}
                        {accessText}
                      </Box>
                    </TableCell>
                    {isSuperAdmin && (
                      <TableCell align="right">
                        <Tooltip title={translate(nameof.full<I18nPersonasNs>((n) => n.sharing.editAccessTooltip))}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenAccessDialog(company)}
                          >
                            <EditAccessIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={translate(nameof.full<I18nPersonasNs>((n) => n.sharing.removeAccessTooltip))}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenUnshareDialog(company)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Access Level Dialog */}
      <Dialog
        open={isAccessDialogOpen}
        onClose={handleCloseAccessDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{translate(nameof.full<I18nPersonasNs>((n) => n.sharing.updateAccessLevel))}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            {translate(nameof.full<I18nPersonasNs>((n) => n.sharing.updateAccessLevelFor))} {selectedCompany ? getCompanyName(selectedCompany.companyId!) : ''}
          </Typography>
          <FormControl component="fieldset" sx={{ mt: 2 }}>
            <RadioGroup
              value={selectedAccess}
              onChange={(e) => setSelectedAccess(e.target.value as AccessLevel)}
            >
              <FormControlLabel
                value={AccessLevel.CanRead}
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ViewIcon fontSize="small" sx={{ mr: 1 }} />
                    {translate(nameof.full<I18nPersonasNs>((n) => n.accessLevels.canRead))}
                  </Box>
                }
              />
              <FormControlLabel
                value={AccessLevel.CanEdit}
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EditIcon fontSize="small" sx={{ mr: 1 }} />
                    {translate(nameof.full<I18nPersonasNs>((n) => n.accessLevels.canEdit))}
                  </Box>
                }
              />
              <FormControlLabel
                value={AccessLevel.CanAdministrate}
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AdminIcon fontSize="small" sx={{ mr: 1 }} />
                    {translate(nameof.full<I18nPersonasNs>((n) => n.accessLevels.canAdministrate))}
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAccessDialog}>{translateCommon(nameof.full<I18nCommonNs>((n) => n.buttonLabels.cancel))}</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateAccess}
            disabled={updateAccessMutation.isLoading}
            startIcon={updateAccessMutation.isLoading ? <CircularProgress size={16} /> : null}
          >
            {translate(nameof.full<I18nPersonasNs>((n) => n.sharing.updateAccess))}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unshare Confirmation Dialog */}
      <Dialog
        open={isUnshareDialogOpen}
        onClose={handleCloseUnshareDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{translate(nameof.full<I18nPersonasNs>((n) => n.sharing.removeCompanyAccess))}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {translate(nameof.full<I18nPersonasNs>((n) => n.sharing.removeAccessWarning))}
          </Alert>
          <Typography variant="body2">
            {translate(nameof.full<I18nPersonasNs>((n) => n.sharing.removeAccessConfirmation))} {companyToUnshare ? getCompanyName(companyToUnshare.companyId!) : ''}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUnshareDialog}>{translateCommon(nameof.full<I18nCommonNs>((n) => n.buttonLabels.cancel))}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmUnshare}
            disabled={unshareMutation.isLoading}
            startIcon={unshareMutation.isLoading ? <CircularProgress size={16} /> : null}
          >
            {translate(nameof.full<I18nPersonasNs>((n) => n.sharing.removeAccess))}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

SharedCompaniesDisplay.displayName = 'SharedCompaniesDisplay';

export default SharedCompaniesDisplay;
